/**
 * Member Portal — standalone customer-facing dashboard for Black Ridge
 * maintenance plan members.
 *
 * Routes:
 *   /members          — token-based dashboard (token via ?token=… or magic-link prop)
 *   /members/login    — magic-link request form (anti-enumeration response)
 *
 * Notes:
 * - Phase C is no-SendGrid, so request-link returns a polite "your account
 *   owner will share a secure link" message. The owner copies the link
 *   from the CRM Members view and delivers it manually until Phase D.
 * - Tokens are single-use (consumed_at server-side) and expire in 72h.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, Route, Routes, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, CalendarCheck, Crown, Loader2, Mail, ShieldCheck, Sparkles, XCircle } from "lucide-react";
import { toast } from "sonner";

const BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api/crm";

function StatusBadge({ status }) {
  const variants = {
    active: "border-emerald-500/40 text-emerald-300",
    pending: "border-amber-500/40 text-amber-300",
    past_due: "border-rose-500/40 text-rose-300",
    cancelled: "border-zinc-600 text-zinc-400",
    expired: "border-zinc-700 text-zinc-500",
  };
  return (
    <Badge variant="outline" className={variants[status] || "border-zinc-700 text-zinc-300"} data-testid={`status-${status}`}>
      {status}
    </Badge>
  );
}

function LoginView() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Email required");
      return;
    }
    setBusy(true);
    try {
      const url = `${BASE}/portal/request-link?email=${encodeURIComponent(email.trim())}`;
      const res = await fetch(url, { method: "POST" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSent(true);
    } catch (exc) {
      toast.error(exc.message || "Request failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dark min-h-screen bg-black text-white flex flex-col">
      <div className="max-w-md w-full mx-auto px-4 sm:px-6 py-12 flex-1 flex flex-col justify-center">
        <Link to="/" className="text-xs uppercase tracking-[0.18em] text-amber-300 hover:text-amber-200 inline-flex items-center gap-1 mb-6">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to site
        </Link>
        <div className="text-center mb-8">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-md bg-gradient-to-br from-amber-400 to-amber-600 text-black font-black">BR</div>
          <h1 className="mt-4 text-2xl font-semibold tracking-tight">Member Portal</h1>
          <p className="text-sm text-zinc-400 mt-1">Sign in to view your plan, upcoming visits, and billing.</p>
        </div>
        {sent ? (
          <Card className="bg-black/40 border-zinc-800">
            <CardContent className="py-6 text-center space-y-3">
              <ShieldCheck className="h-8 w-8 text-emerald-300 mx-auto" />
              <p className="text-white">If a member account exists for <span className="text-amber-300">{email}</span>, your account owner will share a secure link with you shortly.</p>
              <p className="text-xs text-zinc-500">Single-use links expire after 72 hours.</p>
              <Button variant="outline" className="border-zinc-700 text-white" onClick={() => { setSent(false); setEmail(""); }} data-testid="reset-portal-login">
                Send another link
              </Button>
            </CardContent>
          </Card>
        ) : (
          <form onSubmit={submit} className="space-y-4 bg-black/40 border border-zinc-800 rounded-xl p-6">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-zinc-400">Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="bg-zinc-950 border-zinc-700 text-white placeholder:text-zinc-500" data-testid="portal-login-email" />
            </div>
            <Button type="submit" disabled={busy} className="w-full bg-amber-500 text-black hover:bg-amber-400" data-testid="portal-login-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Mail className="h-4 w-4 mr-1" /> Request portal link</>}
            </Button>
            <p className="text-[11px] text-zinc-500 text-center">
              For your security we never confirm whether an account exists. Your account owner will deliver the link manually during our beta.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

function DashboardView() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Sync derived state: if there is no token, we know immediately that we'll
  // show the error UI. Doing this without setState avoids react-hooks rule.
  const missingToken = !token;

  useEffect(() => {
    if (missingToken) {
      return undefined;
    }
    let active = true;
    (async () => {
      try {
        const res = await fetch(`${BASE}/portal/session?token=${encodeURIComponent(token)}`);
        if (!res.ok) throw new Error("Link expired or already used");
        const data = await res.json();
        if (active) setSession(data);
      } catch (exc) {
        if (active) setError(exc.message || "Failed to load portal");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [token, missingToken]);

  const upcoming = useMemo(() => {
    if (!session) return [];
    const today = new Date().toISOString().slice(0, 10);
    return (session.dispatches || [])
      .filter((d) => d.scheduled_date >= today && ["scheduled", "due"].includes(d.status))
      .sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date));
  }, [session]);

  const past = useMemo(() => {
    if (!session) return [];
    return (session.dispatches || [])
      .filter((d) => ["completed", "skipped"].includes(d.status))
      .sort((a, b) => b.scheduled_date.localeCompare(a.scheduled_date))
      .slice(0, 6);
  }, [session]);

  const requestCancel = async () => {
    if (!session?.member?.id) return;
    if (!window.confirm("Request cancellation of your plan? Your account owner will confirm with you.")) return;
    try {
      const url = `${BASE}/portal/cancel?member_id=${encodeURIComponent(session.member.id)}&reason=${encodeURIComponent("Member portal cancellation request")}`;
      const res = await fetch(url, { method: "POST" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      // Token is already consumed, so just merge the cancel update into local state.
      setSession((prev) => prev ? { ...prev, member: { ...prev.member, ...(data.member || {}) } } : prev);
      toast.success("Cancellation requested — your account owner will follow up.");
    } catch (exc) {
      toast.error(exc.message || "Request failed");
    }
  };

  if (missingToken) {
    return (
      <div className="dark min-h-screen bg-black text-white grid place-items-center px-6">
        <div className="max-w-md text-center space-y-4">
          <XCircle className="h-10 w-10 text-rose-300 mx-auto" />
          <p className="text-white">Missing portal token. Request a fresh link.</p>
          <Button onClick={() => navigate("/members/login")} className="bg-amber-500 text-black hover:bg-amber-400" data-testid="portal-restart-btn">
            Request a new link
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="dark min-h-screen bg-black text-white grid place-items-center">
        <div className="flex items-center gap-2 text-zinc-400"><Loader2 className="h-4 w-4 animate-spin" /> Loading portal…</div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="dark min-h-screen bg-black text-white grid place-items-center px-6">
        <div className="max-w-md text-center space-y-4">
          <XCircle className="h-10 w-10 text-rose-300 mx-auto" />
          <p className="text-white">{error || "Portal unavailable"}</p>
          <Button onClick={() => navigate("/members/login")} className="bg-amber-500 text-black hover:bg-amber-400" data-testid="portal-restart-btn">
            Request a new link
          </Button>
        </div>
      </div>
    );
  }

  const { member, tier } = session;
  const nextVisit = upcoming[0] || null;

  return (
    <div className="dark min-h-screen bg-black text-white" data-testid="member-portal">
      <div className="border-b border-zinc-800 bg-black/80 backdrop-blur">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-md bg-gradient-to-br from-amber-400 to-amber-600 grid place-items-center text-black font-black text-sm">BR</div>
            <div>
              <div className="text-sm font-semibold tracking-wide text-white">Member Portal</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Black Ridge Construction</div>
            </div>
          </div>
          <Link to="/" className="text-xs text-zinc-400 hover:text-amber-300 underline-offset-4 hover:underline">Back to site</Link>
        </div>
      </div>

      <div className="max-w-[1100px] mx-auto px-4 sm:px-6 py-8 space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Welcome, {member.customer_name}</h1>
            <p className="text-sm text-zinc-400 mt-1">{member.customer_email} · {member.city || ""}{member.zip_code ? `, ${member.zip_code}` : ""}</p>
          </div>
          <StatusBadge status={member.status} />
        </div>

        {/* Plan summary */}
        <Card className="bg-black/40 border-zinc-800" data-testid="portal-plan-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-white flex items-center gap-2"><Crown className="h-4 w-4 text-amber-300" /> Your plan</CardTitle>
          </CardHeader>
          <CardContent className="grid sm:grid-cols-3 gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-wide text-zinc-500">Tier</div>
              <div className="text-lg font-semibold text-white">{tier?.display_name || member.tier_slug}</div>
              <div className="text-xs text-zinc-400 mt-0.5">{member.billing_cadence}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wide text-zinc-500">Pest service</div>
              <div className="text-lg font-semibold text-white">{member.includes_pest_control ? "Included" : "Not included"}</div>
              {member.includes_pest_control && <div className="text-xs text-zinc-400 mt-0.5">Bi-monthly visits</div>}
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wide text-zinc-500">{member.cancel_at_period_end ? "Ends" : "Renews"}</div>
              <div className="text-lg font-semibold text-white">{member.current_period_end_iso ? new Date(member.current_period_end_iso).toLocaleDateString() : "—"}</div>
              {member.cancel_at_period_end && <div className="text-xs text-rose-300 mt-0.5">Cancellation pending</div>}
            </div>
          </CardContent>
        </Card>

        {/* Upcoming + history */}
        <div className="grid sm:grid-cols-2 gap-4">
          <Card className="bg-black/40 border-zinc-800" data-testid="portal-next-visit">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-white flex items-center gap-2"><Sparkles className="h-4 w-4 text-amber-300" /> Next pest visit</CardTitle>
            </CardHeader>
            <CardContent>
              {member.includes_pest_control && nextVisit ? (
                <div className="space-y-1">
                  <div className="text-lg font-semibold text-white">{nextVisit.completion_window_label}</div>
                  <div className="text-xs text-zinc-400">Scheduled {nextVisit.scheduled_date}</div>
                  <div className="text-xs text-zinc-500 mt-2">Visit #{nextVisit.sequence}. We&rsquo;ll text you the morning of.</div>
                </div>
              ) : member.includes_pest_control ? (
                <p className="text-sm text-zinc-400">No upcoming visit scheduled. Reach out and we&rsquo;ll get one on the books.</p>
              ) : (
                <p className="text-sm text-zinc-400">Pest control is not part of your plan. Upgrade to Plus or Premier to add bi-monthly Duo Pest Defense visits.</p>
              )}
            </CardContent>
          </Card>

          <Card className="bg-black/40 border-zinc-800" data-testid="portal-history">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-white flex items-center gap-2"><CalendarCheck className="h-4 w-4 text-amber-300" /> Recent visits</CardTitle>
            </CardHeader>
            <CardContent>
              {past.length === 0 ? (
                <p className="text-sm text-zinc-400">No completed visits yet.</p>
              ) : (
                <ul className="divide-y divide-zinc-800">
                  {past.map((d) => (
                    <li key={d.id} className="py-2 flex items-center justify-between gap-2 text-sm" data-testid={`portal-visit-${d.id}`}>
                      <div>
                        <div className="text-white">{d.scheduled_date}</div>
                        <div className="text-[11px] text-zinc-500">{d.technician_name ? `${d.technician_name}` : ""}{d.technician_company ? ` · ${d.technician_company}` : ""}</div>
                      </div>
                      <Badge variant="outline" className={d.status === "completed" ? "border-emerald-500/40 text-emerald-300" : "border-zinc-600 text-zinc-400"}>
                        {d.status}
                      </Badge>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Account actions */}
        <Card className="bg-black/40 border-zinc-800" data-testid="portal-actions">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-white">Account actions</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-3">
            <Button asChild variant="outline" className="border-zinc-700 text-white hover:bg-zinc-900">
              <a href={`mailto:hello@blackrdg.com?subject=Plan%20question%20-%20${encodeURIComponent(member.customer_name)}`}>
                <Mail className="h-4 w-4 mr-1" /> Contact us
              </a>
            </Button>
            {!member.cancel_at_period_end && member.status === "active" && (
              <Button variant="outline" className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10" onClick={requestCancel} data-testid="portal-cancel-btn">
                <XCircle className="h-4 w-4 mr-1" /> Request cancellation
              </Button>
            )}
          </CardContent>
        </Card>

        <p className="text-[11px] text-zinc-500 text-center max-w-2xl mx-auto">
          Black Ridge Construction (TX Residential Contractor). Bi-monthly pest control services provided by an independently licensed partner and included with active Plus or Premier plan subscriptions. Service ends with subscription cancellation.
        </p>
      </div>
    </div>
  );
}

export default function MemberPortal() {
  return (
    <Routes>
      <Route path="login" element={<LoginView />} />
      <Route index element={<DashboardView />} />
    </Routes>
  );
}
